import { Pipe, PipeTransform } from '@angular/core';
import { FormControl } from '@angular/forms';

@Pipe({
  name: 'FormatPipe',
})
export class FormatPipe implements PipeTransform {

  transform(str: any, noCurrency: boolean, exchange: string = '01'): any {
    const { negativeSign, integerPart, decimalPart } = this.normalizeAmountParts(str);
    const integerFormat = this.addThousandsSeparator(integerPart);
    const valueFormatted = `${negativeSign}${integerFormat}${decimalPart ? ',' + decimalPart : ''}`;
    return !!noCurrency ? valueFormatted : this.assignCurrency(exchange, valueFormatted);
  }

  assignCurrency(exchange: string, amount: string): string {
    if (parseInt(exchange) === 1) return `₡ ${amount}`;
    else
      if (parseInt(exchange) === 2) return `$ ${amount}`;
      else return `N ${amount}`;
  }

  deleteComa(price: string): number {
    return Number(this.normalizeToNumberString(price));
  }

  deleteCurrencyComa(value: string): number {
    let valueChange: any = value.split(' ');
    if (valueChange?.length > 1) {
      valueChange = this.deleteComa(valueChange[1]);
      return valueChange;
    }
    return Number(value);
  }

  printDisc(noComma: string, ticket): any { return (this.deleteComa(noComma) * ticket.discount); }
  getTotal(noComma: string, ticket): any {
    const price: number = this.deleteComa(noComma);
    return this.transform(price-this.printDisc(noComma, ticket), false);
  }

  deleteCharacters(control: FormControl, first: boolean, noCurrency: boolean): string {
    let value: string = first || noCurrency ? control.value : `${control.value}`.slice(2);
    if(!`${value}`.includes('.')){
      for(let i = 0; i < value.length; i++){
        if(i === 0 && value[0] === '0'){
          value = value.slice(1);
          i--;
        } else {
          break;
        }
      }
    }

    return value;
  }

  changesAmountInputs(control: FormControl, first: boolean, noCurrency: boolean, currencyType = '01'): void {
    const value = this.deleteCharacters(control, first, noCurrency);
    const amount = first ? this.deleteComa(value) : this.deleteComaTyping(value);
    return this.transform(amount, noCurrency, currencyType);
  }

  private deleteComaTyping(value: string): number {
    let stringValue = `${value ?? ''}`.trim();
    if (!stringValue) {
      return 0;
    }

    stringValue = stringValue.replace(/[^\d.,-]/g, '');
    const isNegative = stringValue.includes('-');
    stringValue = stringValue.replace(/-/g, '');

    // While typing we display thousands with "." and decimals with ",".
    // If the user deletes digits from a formatted thousand value (e.g. 1.000 -> 1.00),
    // keep interpreting "." as thousands to avoid converting it into decimals.
    if (!stringValue.includes(',') && stringValue.includes('.')) {
      const dots = stringValue.match(/\./g) || [];
      if (dots.length > 1) {
        stringValue = stringValue.replace(/\./g, '');
      } else {
        const [left = '', right = ''] = stringValue.split('.');
        if (right.length === 3 || left.length <= 3) {
          stringValue = `${left}${right}`;
        }
      }
    }

    return Number(this.normalizeToNumberString(`${isNegative ? '-' : ''}${stringValue}`));
  }

  private normalizeAmountParts(value: any): { negativeSign: string; integerPart: string; decimalPart: string } {
    const numberString = this.normalizeToNumberString(value);
    const isNegative = numberString.startsWith('-');
    const unsignedValue = isNegative ? numberString.slice(1) : numberString;
    const [integerRaw, decimalRaw = ''] = unsignedValue.split('.');
    return {
      negativeSign: isNegative ? '-' : '',
      integerPart: integerRaw || '0',
      decimalPart: decimalRaw.slice(0, 2)
    };
  }

  private addThousandsSeparator(value: string): string {
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  private normalizeToNumberString(value: any): string {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return `${value}`;
    }
    
    let stringValue = `${value ?? 0}`.trim();
    if (!stringValue) {
      return '0';
    }

    stringValue = stringValue.replace(/[^\d.,-]/g, '');
    const isNegative = stringValue.includes('-');
    stringValue = stringValue.replace(/-/g, '');

    const lastDot = stringValue.lastIndexOf('.');
    const lastComma = stringValue.lastIndexOf(',');
    const decimalIndex = Math.max(lastDot, lastComma);

    let integerPart = stringValue;
    let decimalPart = '';

    if (decimalIndex !== -1) {
      const decimalCandidate = stringValue.slice(decimalIndex + 1).replace(/[.,]/g, '');
      const hasMoreThanOneSeparator = (stringValue.match(/[.,]/g) || []).length > 1;
      const isDecimalSeparator = decimalCandidate.length > 0
        && decimalCandidate.length <= 2
        && (hasMoreThanOneSeparator || decimalIndex !== lastDot || decimalCandidate.length !== 3);

      if (isDecimalSeparator) {
        integerPart = stringValue.slice(0, decimalIndex);
        decimalPart = decimalCandidate;
      }
    }

    integerPart = integerPart.replace(/[.,]/g, '') || '0';
    const normalizedValue = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;

    return `${isNegative ? '-' : ''}${normalizedValue}`;
  }
}
